import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { COLORS, FONTS } from '../../constants/config';
import { useAuth } from '../../contexts/AuthContext';
import OrgChartFlow from './OrgChartFlow';
import OrgChartColorSettings from './OrgChartColorSettings';
import type { TreeNode } from './orgChartUtils';

export interface OrganizationalChartProps {
  onNodePress?: (node: TreeNode) => void;
  onDirectoratePress?: (node: TreeNode) => void;
  onDepartmentPress?: (node: TreeNode) => void;
  onMoveComplete?: () => void;
  organizationName?: string;
  organizationId?: number;
  /** Set of unit IDs the current user can drill into. Nodes inside this set
   *  render green and are clickable; nodes outside are gray and inert. When
   *  omitted, every node is treated as in-scope (legacy behaviour). */
  inScopeIds?: Set<number>;
}

// Static CSS for drop target highlighting — injected once via useEffect
const DROP_TARGET_CSS = `
  .org-drop-child > div {
    outline: 3px solid #22C55E !important;
    outline-offset: 2px;
    border-radius: 10px;
    box-shadow: 0 0 16px rgba(34, 197, 94, 0.4) !important;
  }
  .org-drop-sibling > div {
    outline: 3px solid #3B82F6 !important;
    outline-offset: 2px;
    border-radius: 10px;
    box-shadow: 0 0 16px rgba(59, 130, 246, 0.4) !important;
  }
`;

export default function OrganizationalChart({
  onNodePress,
  onMoveComplete,
  organizationName,
  organizationId,
  inScopeIds,
}: OrganizationalChartProps) {
  const { user } = useAuth();
  const resolvedOrgId = organizationId || user?.profile?.organization;
  // DTS 2.2 row "إدارة أعضاء الفريق وتوزيع المهام": الموظف = —, رئيس
  // القسم = إنشاء/قراءة/تعديل. The legacy line said
  //   `canEdit = position_type === 'EMPLOYEE'`
  // which inverted the rule — every plain Employee saw the "تعديل
  // الهيكل" button and the Section Head who actually owns the team
  // didn't. Now keyed on the real perm: only users with team.manage
  // (Section Head + superuser per setup_hrcpms_permissions.py) can
  // restructure the org tree.
  const canEdit = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.team.manage')
  );
  const [showColors, setShowColors] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [moveCount, setMoveCount] = useState(0);
  const [isUndoing, setIsUndoing] = useState(false);
  const confirmRef = useRef<(() => void) | null>(null);
  const undoRef = useRef<(() => Promise<void>) | null>(null);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setShowColors(false);
    setMoveCount(0);
  }, []);

  const handleSave = useCallback(() => {
    if (confirmRef.current) {
      confirmRef.current();
    }
    setIsEditing(false);
    setMoveCount(0);
  }, []);

  const handleCancel = useCallback(async () => {
    if (undoRef.current && moveCount > 0) {
      setIsUndoing(true);
      await undoRef.current();
      setIsUndoing(false);
    }
    setIsEditing(false);
    setMoveCount(0);
  }, [moveCount]);

  // Inject CSS once
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const style = document.createElement('style');
    style.textContent = DROP_TARGET_CSS;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const handleColorsSaved = useCallback(() => {
    setShowColors(false);
    setRefreshKey((k) => k + 1);
  }, []);

  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* The org structure is sourced from SSO and rendered read-only. The
          edit ("تعديل الهيكل") and structure-color ("ألوان الهيكل") controls
          were removed so the hierarchy can't be restructured/recolored in
          CPMS — it mirrors SSO (and is re-synced from SSO on every deploy). */}
      {/* React Flow container */}
      <View style={styles.chartContainer}>
        <OrgChartFlow
          key={refreshKey}
          organizationId={resolvedOrgId}
          organizationName={organizationName}
          onNodePress={onNodePress}
          onMoveComplete={onMoveComplete}
          isEditing={false}
          onMoveCountChange={setMoveCount}
          confirmRef={confirmRef}
          undoRef={undoRef}
          inScopeIds={inScopeIds}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  toolbar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 8,
    paddingHorizontal: 12,
    backgroundColor: '#FAFAFA',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: '#E5E7EB',
  },
  editToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    justifyContent: 'flex-end',
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#3B82F6',
    backgroundColor: '#EFF6FF',
  },
  editBtnText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#3B82F6',
  },
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 6,
    backgroundColor: '#22C55E',
  },
  saveBtnDisabled: {
    backgroundColor: '#9CA3AF',
  },
  saveBtnText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
  },
  cancelBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  cancelBtnText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  editHint: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    marginRight: 'auto' as any,
  },
  colorBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  colorBtnActive: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6',
  },
  colorBtnEmoji: {
    fontSize: 16,
  },
  colorBtnText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#374151',
  },
  colorPanelWrapper: {
    position: 'absolute',
    top: 44,
    right: 12,
    zIndex: 9999,
    elevation: 9999,
  },
  chartContainer: {
    width: '100%',
    height: 560,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    backgroundColor: '#FAFAFA',
  },
});
